import { create } from 'zustand';
import { useEffect } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';
import { clsx } from 'clsx';

interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface ToastStore {
  toasts: ToastItem[];
  addToast: (type: ToastItem['type'], message: string) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (type, message) => {
    const id = Date.now().toString();
    set((s) => ({ toasts: [...s.toasts, { id, type, message }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 4000);
  },
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function toast(type: ToastItem['type'], message: string) {
  useToastStore.getState().addToast(type, message);
}

export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();
  return (
    <div className="fixed inset-x-4 top-[calc(0.75rem+env(safe-area-inset-top))] z-[100] flex flex-col items-stretch gap-2 sm:inset-x-auto sm:right-4 sm:top-4 sm:items-end">
      {toasts.map((t) => (
        <ToastMessage key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}
    </div>
  );
}

function ToastMessage({ toast: t, onClose }: { toast: ToastItem; onClose: () => void }) {
  const icons = { success: <CheckCircle size={18} />, error: <XCircle size={18} />, info: <Info size={18} /> };
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={clsx(
      'flex items-center gap-3 px-4 py-3 rounded-xl border border-dark-border bg-dark-elevated shadow-lg animate-fade-in sm:min-w-[320px] sm:max-w-md',
    )}>
      <span className={clsx('shrink-0', t.type === 'success' && 'text-success', t.type === 'error' && 'text-danger', t.type === 'info' && 'text-primary')}>{icons[t.type]}</span>
      <span className="flex-1 text-sm text-dark-text">{t.message}</span>
      <button onClick={onClose} className="text-dark-muted hover:text-dark-text cursor-pointer"><X size={14} /></button>
    </div>
  );
}
